/**
 * Mem0 客户端封装
 * 提供 Gongfu 项目的记忆管理核心功能
 */

import { Memory as Mem0Client } from 'mem0ai'
import type {
  MemoryConfig,
  MemoryEntry,
  MemoryMetadata,
  SearchOptions,
  SearchResult,
  SearchFilter,
  MemoryExport,
  MemoryCategory
} from './types'

/**
 * 记忆客户端
 * 封装 mem0 的核心功能，提供类型安全的接口
 */
export class MemoryClient {
  private client: Mem0Client
  private config: MemoryConfig
  private defaultUserId: string
  private defaultProjectId?: string

  constructor(config: MemoryConfig) {
    this.config = config
    this.defaultUserId = config.userId || 'default'
    this.defaultProjectId = config.projectId

    // 初始化 mem0 客户端
    this.client = new Mem0Client({
      api_key: config.apiKey,
      ...config.config
    })
  }

  /**
   * 添加记忆
   */
  async add(
    content: string,
    metadata?: Partial<MemoryMetadata>
  ): Promise<MemoryEntry> {
    const fullMetadata: MemoryMetadata = {
      userId: this.defaultUserId,
      projectId: this.defaultProjectId,
      ...metadata,
      tags: metadata?.tags || [],
      createdAt: new Date(),
      updatedAt: new Date()
    }

    // 调用 mem0 添加记忆
    const result = await this.client.add(content, {
      user_id: fullMetadata.userId,
      metadata: this.serializeMetadata(fullMetadata)
    })

    return this.parseMemoryResult(result)
  }

  /**
   * 批量添加记忆
   */
  async batchAdd(
    memories: Array<{ content: string; metadata?: Partial<MemoryMetadata> }>
  ): Promise<MemoryEntry[]> {
    const results = await Promise.all(
      memories.map(({ content, metadata }) => this.add(content, metadata))
    )
    return results
  }

  /**
   * 搜索记忆
   */
  async search(
    query: string,
    options?: SearchOptions
  ): Promise<SearchResult[]> {
    const searchParams: any = {
      query,
      user_id: options?.filter?.userId || this.defaultUserId,
      limit: options?.limit || 10
    }

    // 添加过滤条件
    if (options?.filter) {
      searchParams.filters = this.buildFilters(options.filter)
    }

    const results = await this.client.search(searchParams)
    
    return results.map((result: any) => ({
      memory: this.parseMemoryResult(result),
      score: result.score || 0,
      highlights: result.highlights
    }))
  }

  /**
   * 批量搜索
   */
  async batchSearch(
    queries: string[],
    options?: SearchOptions
  ): Promise<SearchResult[][]> {
    const results = await Promise.all(
      queries.map(query => this.search(query, options))
    )
    return results
  }

  /**
   * 获取特定记忆
   */
  async get(memoryId: string): Promise<MemoryEntry | null> {
    try {
      const result = await this.client.get(memoryId)
      return result ? this.parseMemoryResult(result) : null
    } catch (error) {
      console.error('获取记忆失败:', error)
      return null
    }
  }

  /**
   * 更新记忆
   */
  async update(
    memoryId: string,
    content: string,
    metadata?: Partial<MemoryMetadata>
  ): Promise<MemoryEntry> {
    const existingMemory = await this.get(memoryId)
    if (!existingMemory) {
      throw new Error(`记忆不存在: ${memoryId}`)
    }

    const updatedMetadata: MemoryMetadata = {
      ...existingMemory.metadata,
      ...metadata,
      updatedAt: new Date()
    }

    const result = await this.client.update(memoryId, {
      data: content,
      metadata: this.serializeMetadata(updatedMetadata)
    })

    return this.parseMemoryResult(result)
  }

  /**
   * 删除记忆
   */
  async delete(memoryId: string): Promise<boolean> {
    try {
      await this.client.delete(memoryId)
      return true
    } catch (error) {
      console.error('删除记忆失败:', error)
      return false
    }
  }

  /**
   * 删除所有记忆
   */
  async deleteAll(filter?: SearchFilter): Promise<number> {
    // 先搜索符合条件的记忆
    const memories = await this.search('*', { filter, limit: 10000 })
    
    // 批量删除
    const deletePromises = memories.map(({ memory }) => 
      this.delete(memory.id)
    )
    
    const results = await Promise.all(deletePromises)
    return results.filter(success => success).length
  }

  /**
   * 导出用户记忆
   */
  async exportUserMemories(userId?: string): Promise<MemoryExport> {
    const targetUserId = userId || this.defaultUserId
    
    // 获取所有记忆
    const memories = await this.search('*', {
      filter: { userId: targetUserId },
      limit: 10000
    })

    // 统计信息
    const byCategory: Record<string, number> = {}
    let oldestDate = new Date()
    let newestDate = new Date(0)

    memories.forEach(({ memory }) => {
      const category = memory.metadata.category || 'other'
      byCategory[category] = (byCategory[category] || 0) + 1
      
      if (memory.createdAt < oldestDate) oldestDate = memory.createdAt
      if (memory.createdAt > newestDate) newestDate = memory.createdAt
    })

    return {
      version: '1.0',
      exportedAt: new Date(),
      userId: targetUserId,
      projectId: this.defaultProjectId,
      memories: memories.map(({ memory }) => memory),
      statistics: {
        totalCount: memories.length,
        byCategory: byCategory as Record<MemoryCategory, number>,
        dateRange: {
          oldest: oldestDate,
          newest: newestDate
        }
      }
    }
  }

  /**
   * 导入记忆
   */
  async importMemories(data: MemoryExport): Promise<number> {
    let imported = 0
    
    for (const memory of data.memories) {
      try {
        await this.add(memory.content, memory.metadata)
        imported++
      } catch (error) {
        console.error('导入记忆失败:', error, memory)
      }
    }
    
    return imported
  }

  /**
   * 清除用户记忆
   */
  async clearUserMemories(userId?: string): Promise<number> {
    return this.deleteAll({ userId: userId || this.defaultUserId })
  }

  /**
   * 清除项目记忆
   */
  async clearProjectMemories(projectId?: string): Promise<number> {
    return this.deleteAll({ 
      projectId: projectId || this.defaultProjectId 
    })
  }

  /**
   * 获取记忆统计
   */
  async getStats(filter?: SearchFilter): Promise<{
    total: number
    byCategory: Record<MemoryCategory, number>
    byUser: Record<string, number>
    averageScore: number
  }> {
    const memories = await this.search('*', { filter, limit: 10000 })
    
    const byCategory: Record<string, number> = {}
    const byUser: Record<string, number> = {}
    let totalScore = 0

    memories.forEach(({ memory, score }) => {
      const category = memory.metadata.category || 'other'
      byCategory[category] = (byCategory[category] || 0) + 1
      
      const userId = memory.metadata.userId
      byUser[userId] = (byUser[userId] || 0) + 1
      
      totalScore += score
    })

    return {
      total: memories.length,
      byCategory: byCategory as Record<MemoryCategory, number>,
      byUser,
      averageScore: memories.length > 0 ? totalScore / memories.length : 0
    }
  }

  /**
   * 预热缓存
   */
  async warmCache(options: {
    userId?: string
    categories?: MemoryCategory[]
    limit?: number
  }): Promise<void> {
    const { userId, categories, limit = 100 } = options
    
    // 预加载常用记忆
    const filter: SearchFilter = {
      userId: userId || this.defaultUserId
    }
    
    if (categories?.length) {
      filter.category = categories
    }
    
    await this.search('*', { filter, limit })
  }

  // ============= 私有方法 =============

  /**
   * 序列化元数据
   */
  private serializeMetadata(metadata: MemoryMetadata): Record<string, any> {
    return {
      ...metadata,
      createdAt: metadata.createdAt?.toISOString(),
      updatedAt: metadata.updatedAt?.toISOString(),
      lastAccessedAt: metadata.lastAccessedAt?.toISOString()
    }
  }

  /**
   * 解析记忆结果
   */
  private parseMemoryResult(result: any): MemoryEntry {
    const metadata = result.metadata || {}
    
    return {
      id: result.id || result.memory_id,
      content: result.memory || result.data || result.content,
      embedding: result.embedding,
      metadata: {
        userId: metadata.userId || this.defaultUserId,
        projectId: metadata.projectId || this.defaultProjectId,
        category: metadata.category,
        tags: metadata.tags || [],
        ttl: metadata.ttl,
        importance: metadata.importance,
        accessCount: metadata.accessCount || 0,
        lastAccessedAt: metadata.lastAccessedAt 
          ? new Date(metadata.lastAccessedAt) 
          : undefined,
        createdAt: metadata.createdAt 
          ? new Date(metadata.createdAt) 
          : new Date(),
        updatedAt: metadata.updatedAt 
          ? new Date(metadata.updatedAt) 
          : new Date()
      },
      score: result.score,
      createdAt: new Date(metadata.createdAt || Date.now()),
      updatedAt: new Date(metadata.updatedAt || Date.now())
    }
  }

  /**
   * 构建过滤器
   */
  private buildFilters(filter: SearchFilter): Record<string, any> {
    const filters: Record<string, any> = {}
    
    if (filter.projectId) {
      filters.projectId = filter.projectId
    }
    
    if (filter.category) {
      filters.category = Array.isArray(filter.category) 
        ? { $in: filter.category }
        : filter.category
    }
    
    if (filter.tags?.length) {
      filters.tags = { $in: filter.tags }
    }
    
    if (filter.timeRange) {
      filters.createdAt = {}
      if (filter.timeRange.start) {
        filters.createdAt.$gte = filter.timeRange.start.toISOString()
      }
      if (filter.timeRange.end) {
        filters.createdAt.$lte = filter.timeRange.end.toISOString()
      }
    }
    
    if (filter.importance) {
      filters.importance = {}
      if (filter.importance.min !== undefined) {
        filters.importance.$gte = filter.importance.min
      }
      if (filter.importance.max !== undefined) {
        filters.importance.$lte = filter.importance.max
      }
    }
    
    return filters
  }
}